import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { useBackHomePath } from "@/lib/useBackHomePath";
import { ArrowLeft, Calendar } from "lucide-react";

const DAYS = ["E Hënë", "E Martë", "E Mërkurë", "E Enjte", "E Premte"];
const PERIODS = [
    { p: 1, time: "08:00 – 08:45" },
    { p: 2, time: "08:50 – 09:35" },
    { p: 3, time: "09:40 – 10:25" },
    { p: 4, time: "10:55 – 11:40" },
    { p: 5, time: "11:45 – 12:30" },
    { p: 6, time: "12:35 – 13:20" },
    { p: 7, time: "13:25 – 14:10" },
];

export default function TeacherTimetable() {
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const backPath = useBackHomePath();

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/teacher/timetable");
                setSlots(r.data.slots || []);
            } finally { setLoading(false); }
        })();
    }, []);

    const slotMap = useMemo(() => {
        const m = {};
        for (const s of slots) m[`${s.day}-${s.period}`] = s;
        return m;
    }, [slots]);

    return (
        <Layout>
            <Link to={backPath} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Orari</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Orari im javor</h1>
                <p className="text-slate-600 mt-2 text-sm">Vetëm orët e mia mësimore — të caktuara nga administratori</p>
            </div>

            {loading ? (
                <div className="text-slate-500">Duke ngarkuar…</div>
            ) : slots.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
                    <Calendar className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                    <div className="font-heading text-xl font-bold text-slate-800">Nuk keni orë në orar</div>
                    <p className="text-sm text-slate-600 mt-1">Administratori nuk ju ka caktuar ende lëndë + klasë në orar.</p>
                </div>
            ) : (
                <div className="overflow-x-auto bg-white border border-slate-200 rounded-2xl">
                    <table className="w-full min-w-[640px] border-collapse" data-testid="teacher-tt-grid">
                        <thead>
                            <tr className="bg-bone/60 text-[10px] uppercase tracking-widest text-slate-700 font-bold">
                                <th className="text-left px-3 py-2 w-28">Ora / Dita</th>
                                {DAYS.map((d) => <th key={d} className="text-center px-2 py-2">{d}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {PERIODS.map((p) => (
                                <tr key={p.p} className="border-t border-slate-200">
                                    <td className="px-3 py-2 align-middle">
                                        <div className="text-xs font-bold text-oxford">Ora {p.p}</div>
                                        <div className="text-[10px] text-slate-500 font-mono">{p.time}</div>
                                    </td>
                                    {DAYS.map((_, di) => {
                                        const slot = slotMap[`${di}-${p.p}`];
                                        return (
                                            <td key={`${di}-${p.p}`} className="p-1 align-stretch">
                                                {slot ? (
                                                    <div className="rounded-md px-2 py-2 text-white" style={{ background: slot.color || "#1E3A5F" }} data-testid={`teacher-tt-cell-${di}-${p.p}`}>
                                                        <div className="text-xs font-bold leading-tight line-clamp-2">{slot.subject_name}</div>
                                                        <div className="text-[10px] opacity-90 mt-0.5">{slot.class_name}</div>
                                                    </div>
                                                ) : (
                                                    <div className="rounded-md px-2 py-2 min-h-[52px] text-[10px] text-slate-300 italic text-center">—</div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="px-4 py-3 bg-bone/40 border-t border-slate-200 text-[11px] text-slate-600">
                        Total: <strong className="text-oxford">{slots.length}</strong> orë në javë
                    </div>
                </div>
            )}
        </Layout>
    );
}
